import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, UserPlus, Power } from "lucide-react";

const STAGES = ["fabric_cut_pending", "stitching", "finishing", "qc", "ready_for_dispatch"];

export default function MasterManagerDashboard() {
  const { user, loading } = useAuth();
  const [karigars, setKarigars] = useState([]);
  const [queue, setQueue] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", specialty: "all", capacity_per_week: 5 });

  const refresh = async () => {
    try {
      const [k, q] = await Promise.all([api.get("/karigars"), api.get("/bespoke/queue")]);
      setKarigars(k.data); setQueue(q.data);
    } catch (e) { /* role-gated; show empty */ }
  };
  useEffect(() => { if (user && ["super_admin", "major_admin", "admin", "master_manager"].includes(user.role)) refresh(); }, [user]);

  if (loading) return <div className="sq-container py-20 sq-eyebrow">Loading atelier…</div>;
  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in</Link></div>;
  if (!["super_admin", "major_admin", "admin", "master_manager"].includes(user.role)) return <div className="sq-container py-20">Access denied.</div>;

  const create = async () => {
    try { await api.post("/karigars", { ...form, capacity_per_week: Number(form.capacity_per_week) }); toast("Karigar added"); setForm({ ...form, name: "", phone: "" }); refresh(); }
    catch { toast.error("Failed"); }
  };
  const toggle = async (kid) => { await api.post(`/karigars/${kid}/toggle`); refresh(); };
  const autoAllot = async () => { try { const { data } = await api.post("/bespoke/auto-allot"); toast.success(`Auto-allotted ${data.count} orders`); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const manualAllot = async (bid, karigar_id) => { try { await api.post(`/bespoke/${bid}/allot`, { bespoke_id: bid, karigar_id }); toast.success("Allotted"); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const advance = async (bid, stage) => { await api.post(`/bespoke/${bid}/stage`, { stage }); toast.success(`Moved to ${stage}`); refresh(); };

  return (
    <main className="sq-container py-10" data-testid="master-manager-dashboard">
      <div className="flex items-end justify-between border-b border-black/10 pb-8">
        <div>
          <div className="sq-eyebrow text-squire-jeans">Atelier Floor</div>
          <h1 className="font-display text-5xl mt-2">Master Manager</h1>
        </div>
        <button data-testid="mm-auto-allot" onClick={autoAllot} className="sq-btn-primary"><Wand2 size={14} /> Auto-Allot Queue</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        {/* Karigars */}
        <section className="lg:col-span-1">
          <div className="sq-eyebrow mb-3">Karigars · {karigars.length}</div>
          <div className="space-y-2 max-h-[480px] overflow-y-auto sq-scroll">
            {karigars.map((k) => (
              <div key={k.id} className="border border-black/10 p-3 flex items-center gap-3" data-testid={`karigar-${k.id}`}>
                <div className="w-10 h-10 rounded-full bg-squire-jeans text-white flex items-center justify-center text-xs font-bold">{k.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}</div>
                <div className="flex-1">
                  <div className="font-display">{k.name}</div>
                  <div className="sq-eyebrow text-[10px]">{k.id} · {k.specialty} · load {k.current_load}/{k.capacity_per_week}</div>
                </div>
                <button data-testid={`karigar-toggle-${k.id}`} onClick={() => toggle(k.id)} className={`p-2 ${k.active ? "text-squire-jeans" : "text-[#4B5563]"}`}><Power size={14} /></button>
              </div>
            ))}
          </div>

          <div className="mt-6 border border-black/10 p-4">
            <div className="sq-eyebrow flex items-center gap-2"><UserPlus size={12} /> New Karigar</div>
            {[["name", "Name"], ["phone", "Phone"], ["capacity_per_week", "Capacity / week"]].map(([k, l]) => (
              <label key={k} className="block mt-3"><span className="sq-eyebrow text-[10px]">{l}</span>
                <input data-testid={`new-karigar-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
              </label>
            ))}
            <label className="block mt-3"><span className="sq-eyebrow text-[10px]">Specialty</span>
              <select data-testid="new-karigar-specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm">
                {["all", "shirt", "blazer", "suit", "trousers", "ethnic"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <button data-testid="new-karigar-create" onClick={create} className="sq-btn-primary w-full mt-4">Add</button>
          </div>
        </section>

        {/* Queue */}
        <section className="lg:col-span-2">
          <div className="sq-eyebrow mb-3">Bespoke Queue · {queue.length}</div>
          <div className="space-y-2 max-h-[640px] overflow-y-auto sq-scroll">
            {queue.length === 0 && <div className="text-[#4B5563] text-sm">No bespoke orders.</div>}
            {queue.map((b) => (
              <div key={b.id} className="border border-black/10 p-4" data-testid={`queue-${b.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <div className="font-display text-lg">{b.id} · {b.garment_type}</div>
                    <div className="sq-eyebrow text-[10px] mt-1">{b.user_id} · {formatINR(b.total)} · {b.karigar_name ? `→ ${b.karigar_name}` : "unassigned"}</div>
                  </div>
                  <div className="text-xs">
                    <span className="px-2 py-1 bg-squire-jeans text-white">{b.stage}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {!b.karigar_id && (
                    <select data-testid={`allot-select-${b.id}`} className="bg-transparent border border-black/20 text-xs py-1 px-2" onChange={(e) => { if (e.target.value) manualAllot(b.id, e.target.value); }}>
                      <option value="">Allot to karigar…</option>
                      {karigars.filter((k) => k.active).map((k) => <option key={k.id} value={k.id}>{k.name} ({k.specialty})</option>)}
                    </select>
                  )}
                  {STAGES.map((s) => (
                    <button key={s} data-testid={`stage-${b.id}-${s}`} onClick={() => advance(b.id, s)} className={`text-[10px] px-2 py-1 border ${b.stage === s ? "bg-black text-white border-black" : "border-black/30 hover:border-squire-jeans"}`}>
                      {s.replace(/_/g, " ")}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
